/* ============================================================
   Jumbo Blocks — combined script
   Handles: Text Reveal / Button / Price+Button / Image Reveal
   All four block types share the same [data-jumbo-reveal] hook,
   dispatched by data-jumbo-type so each gets the right behavior
   from a single init pass (no duplicate observers, no races).
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Text-splitting helpers (Jumbo Text Reveal only) ---------- */

  function appendWordSpans(fragment, raw) {
    var words = raw.split(' ').filter(function (w) {
      return w.length > 0;
    });
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'jumbo-block__unit';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = word;
      fragment.appendChild(span);
      if (i < words.length - 1) {
        var space = document.createElement('span');
        space.className = 'jumbo-block__space';
        space.setAttribute('aria-hidden', 'true');
        fragment.appendChild(space);
      }
    });
  }

  function appendCharSpans(fragment, raw) {
    Array.prototype.forEach.call(raw, function (ch) {
      if (ch === ' ') {
        var space = document.createElement('span');
        space.className = 'jumbo-block__space';
        space.setAttribute('aria-hidden', 'true');
        fragment.appendChild(space);
      } else if (ch !== '\n' && ch !== '\r') {
        var span = document.createElement('span');
        span.className = 'jumbo-block__unit';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = ch;
        fragment.appendChild(span);
      }
    });
  }

  function splitTextLine(root, mode) {
    if (root.dataset.jumboSplit === mode) return; // already split this way
    var textEl = root.querySelector('[data-jumbo-line-text]');
    if (!textEl) return;

    var ariaLabel = textEl.textContent;
    var originalNodes = Array.prototype.slice.call(textEl.childNodes);
    var fragment = document.createDocumentFragment();

    originalNodes.forEach(function (node) {
      // Preserve manual line breaks (<br>) exactly where they were typed
      if (node.nodeType === 1 && node.tagName === 'BR') {
        fragment.appendChild(document.createElement('br'));
        return;
      }
      var raw = node.textContent;
      if (!raw || !raw.replace(/[\n\r]/g, '').trim().length) return;
      if (mode === 'word-by-word') {
        appendWordSpans(fragment, raw);
      } else if (mode === 'char-by-char') {
        appendCharSpans(fragment, raw);
      }
    });

    textEl.innerHTML = '';
    textEl.setAttribute('aria-label', ariaLabel);
    textEl.appendChild(fragment);
    root.dataset.jumboSplit = mode;
  }

  function applyTextTiming(root) {
    var duration = parseInt(root.dataset.duration, 10) || 700;
    var stagger = parseInt(root.dataset.delayStep, 10) || 40;
    var baseDelay = parseInt(root.dataset.revealDelay, 10) || 0;
    var easing = root.dataset.easing || 'ease-out';
    var textEl = root.querySelector('[data-jumbo-line-text]');
    if (textEl) {
      textEl.style.transitionDuration = duration + 'ms';
      textEl.style.transitionTimingFunction = easing;
      textEl.style.transitionDelay = baseDelay + 'ms';
    }
    var units = root.querySelectorAll('.jumbo-block__unit');
    units.forEach(function (unit, i) {
      unit.style.transitionDuration = duration + 'ms';
      unit.style.transitionTimingFunction = easing;
      unit.style.transitionDelay = (baseDelay + i * stagger) + 'ms';
    });
  }

  function setupTextBlock(root, animation) {
    var isSplitMode = animation === 'word-by-word' || animation === 'char-by-char';
    if (isSplitMode) splitTextLine(root, animation);
    applyTextTiming(root);

    // Reveal the (now split, still individually hidden) text container.
    // Prevents the raw text from ever flashing on screen before JS runs.
    var textEl = root.querySelector('[data-jumbo-line-text]');
    if (textEl) textEl.classList.add('jumbo-ready');
  }

  /* ---------- Shared init: Text / Button / Price+Button / Image ---------- */

  function initBlock(root) {
    if (root.dataset.jumboInit === 'true') return;
    root.dataset.jumboInit = 'true';

    var type = root.dataset.jumboType || 'generic';
    var animation = root.dataset.animation || 'fade-up';
    var replay = root.dataset.replay === 'true';

    if (type === 'text') {
      setupTextBlock(root, animation);
    }

    if (animation === 'none') {
      root.classList.add('is-inview');
      return;
    }

    var threshold = parseFloat(root.dataset.threshold);
    if (isNaN(threshold)) threshold = 0.2;
    threshold = Math.min(Math.max(threshold, 0), 0.95);

    var reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      root.classList.add('is-inview');
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            if (!replay) observer.unobserve(entry.target);
          } else if (replay) {
            entry.target.classList.remove('is-inview');
          }
        });
      },
      { threshold: threshold, rootMargin: '0px 0px -5% 0px' }
    );

    observer.observe(root);
    root._jumboObserver = observer;
  }

  function initAll(context) {
    var scope = context || document;
    var roots =
      scope.matches && scope.matches('[data-jumbo-reveal]')
        ? [scope]
        : scope.querySelectorAll('[data-jumbo-reveal]');
    roots.forEach(initBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.matches('[data-jumbo-reveal]')
      ? event.target
      : event.target.querySelector('[data-jumbo-reveal]');
    if (!root) return;
    root.classList.remove('is-inview');
    window.setTimeout(function () {
      root.classList.add('is-inview');
    }, 150);
  });
})();

/* ============================================================
   Jumbo FAQ Accordion
   Independent of the reveal system above — own [data-jumbo-faq]
   hook, own height-animation logic (measures scrollHeight so the
   accordion can animate smoothly regardless of content length).
   ============================================================ */
(function () {
  'use strict';

  function getReduceMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function openFaqItem(item, panel, inner, trigger, duration) {
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    if (getReduceMotion()) {
      panel.style.height = 'auto';
      return;
    }

    var targetHeight = inner.scrollHeight;
    panel.style.height = '0px';
    // Force reflow so the browser registers the starting height before we animate.
    panel.offsetHeight; // eslint-disable-line no-unused-expressions
    panel.style.height = targetHeight + 'px';

    var onEnd = function (e) {
      if (e.target !== panel || e.propertyName !== 'height') return;
      panel.style.height = 'auto';
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);
  }

  function closeFaqItem(item, panel, trigger) {
    item.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');

    if (getReduceMotion()) {
      panel.style.height = '0px';
      return;
    }

    var startHeight = panel.scrollHeight;
    panel.style.height = startHeight + 'px';
    panel.offsetHeight; // eslint-disable-line no-unused-expressions
    requestAnimationFrame(function () {
      panel.style.height = '0px';
    });
  }

  function initFaqContainer(container) {
    if (container.dataset.jumboFaqInit === 'true') return;
    container.dataset.jumboFaqInit = 'true';

    var allowMultiple = container.dataset.allowMultiple === 'true';
    var openAll = container.dataset.openAll === 'true';
    var duration = parseInt(container.dataset.duration, 10) || 300;
    var easing = container.dataset.easing || 'ease-out';
    var reduceMotion = getReduceMotion();

    var items = Array.prototype.slice.call(
      container.querySelectorAll('[data-faq-item]')
    );

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-faq-trigger]');
      var panel = item.querySelector('[data-faq-panel]');
      var inner = item.querySelector('[data-faq-panel-inner]');
      if (!trigger || !panel || !inner) return;

      panel.style.transitionDuration = reduceMotion ? '0ms' : duration + 'ms';
      panel.style.transitionTimingFunction = easing;

      var startOpen = openAll || panel.dataset.faqDefaultOpen === 'true';
      if (startOpen) {
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = reduceMotion ? 'auto' : inner.scrollHeight + 'px';
        if (!reduceMotion) {
          window.setTimeout(function () {
            panel.style.height = 'auto';
          }, duration);
        }
      } else {
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = '0px';
      }

      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');

        if (isOpen) {
          closeFaqItem(item, panel, trigger);
          return;
        }

        if (!allowMultiple) {
          items.forEach(function (other) {
            if (other === item || !other.classList.contains('is-open')) return;
            var otherTrigger = other.querySelector('[data-faq-trigger]');
            var otherPanel = other.querySelector('[data-faq-panel]');
            if (otherTrigger && otherPanel) {
              closeFaqItem(other, otherPanel, otherTrigger);
            }
          });
        }

        openFaqItem(item, panel, inner, trigger, duration);
      });
    });
  }

  function initAllFaq(context) {
    var scope = context || document;
    var containers =
      scope.matches && scope.matches('[data-jumbo-faq]')
        ? [scope]
        : scope.querySelectorAll('[data-jumbo-faq]');
    containers.forEach(initFaqContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllFaq();
    });
  } else {
    initAllFaq();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllFaq(event.target);
  });
})();
